import { useEffect, useMemo, useState } from "react";
import "./styles.css";
import { Home } from "./views/Home";
import { TdcView } from "./views/TdcView";
import { PhcView } from "./views/PhcView";
import { useGameStore, type GameSession } from "./gameStore";
import { getToken, clearToken, getLastCode, clearLastCode, setLastCode } from "./storage";
import { apiResume } from "./api";

export function App(): JSX.Element {
  const [session, setSession] = useState<GameSession | null>(null);

  // Auto-resume on app boot:
  //   1) if URL has ?code=XXXX with a saved token → resume that game,
  //   2) else if we have a remembered last_code with a token → resume it.
  // On resume failure, drop the stale pointer so we don't loop trying.
  useEffect(() => {
    const url = new URL(window.location.href);
    const urlCode = url.searchParams.get("code");
    const tryCode = urlCode && /^\d{4}$/.test(urlCode) ? urlCode : getLastCode();
    if (!tryCode || !/^\d{4}$/.test(tryCode)) return;
    const token = getToken(tryCode);
    if (!token) return;
    void apiResume(token).then((r) => {
      if (r.ok && r.gameCode && r.participantId && r.role) {
        setLastCode(r.gameCode);
        setSession({
          gameCode: r.gameCode,
          participantId: r.participantId,
          role: r.role,
          token,
        });
      } else {
        clearToken(tryCode);
        if (getLastCode() === tryCode) clearLastCode();
      }
    });
  }, []);

  const initialCode = useMemo(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get("code") ?? undefined;
  }, []);

  const store = useGameStore(session);

  if (!session) {
    return <Home initialCode={initialCode} onSession={setSession} />;
  }

  if (session.role === "TDC") return <TdcView store={store} />;
  return <PhcView store={store} />;
}
